import * as React from "react";
import { Button, Dropdown, Modal, notification, Switch } from "antd";
import { ChevronDown, RefreshCw } from "react-feather";
import { linksOf, request } from "../../services/cpln";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { formatStrings } from "../../utils/time";
import { itemHelpers as itemHelpersBase } from "../../mst/kinds/item.helpers";
import { Agent } from "../../schema/types/agent/entity";
import { useGetAgentQuery } from "../../store/api/agentApi";
import { ViewModal } from "../../components/modals/viewModal";
import { DeleteModal } from "../../components/modals/deleteModal";
import { TerraformImportModal } from "../../components/modals/tfImportModal";
import { K8sCrdExportModal } from "../../components/modals/k8sCrdExportModal";
import { DownloadScriptsModal } from "./downloadScriptsModal";
import { Tooltip } from "../../components/Tooltip";
import { dateString } from "../../components/dateStringFn";
import { DateString } from "../../components/dateString";
import { useAppSelector } from "../../store/hooks";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { selectContext } from "../../store/contextSlice";
import { selectLabel } from "../../store/timezoneSlice";
import { Kind } from "../../schema/types/base";

interface Props {
  agent: Agent;
}
export const AgentDetailHeader: React.FC<Props> = ({ agent }) => {
  const selfLink = linksOf(agent).self!;

  const { refetch, isFetching } = useGetAgentQuery(selfLink, { skip: !selfLink });

  const { org, gvc } = useAppSelector(selectContext);
  const timezoneLabel = useAppSelector(selectLabel);
  const terraformSupportedKinds = useAppSelector((state) => state.rest.terraformSupportedKinds);
  const k8sCrdSupportedKinds = useAppSelector((state) => state.rest.k8sCrdSupportedKinds);

  const [timeKey, setTimeKey] = React.useState(Date.now());
  const [terraformImportCommand, setTerraformImportCommand] = React.useState("");
  const [action, setAction] = React.useState<"view" | "delete" | "lock" | "tfImportCommand" | "k8sCrdExport" | "download-scripts" | "">("");

  const protocolVersion = agent.status?.protocolVersion || "v1";

  const itemHelpers = itemHelpersBase({ org: org, gvc: gvc, kind: agent.kind, name: agent.name });

  function getIsLocked(): boolean {
    return (agent.tags || {})["cpln/protected"] == "true";
  }

  const onLock = async () => {
    try {
      const isLocked = getIsLocked();
      await request({ method: "patch", body: { tags: { "cpln/protected": !isLocked } }, url: selfLink });
      notification.success({
        message: "Success",
        description: `Object was ${isLocked ? "unlocked" : "locked"} successfully`,
      });
      refetch();
      setAction("");
    } catch (e) {
      setAction("");
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  };

  const canExportToTerraform = terraformSupportedKinds.includes(agent.kind as Kind);
  const canExportToK8sCrd = k8sCrdSupportedKinds.includes(agent.kind as Kind);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeKey(Date.now());
    }, 1 * 60 * 1_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  React.useEffect(() => {
    if (terraformImportCommand) {
      setAction("tfImportCommand");
    }
  }, [terraformImportCommand]);

  async function handleActionsClick({ key }: any) {
    switch (key) {
      case "view":
        setAction("view");
        break;
      case "export-json":
        await itemHelpers.handleExportJSON([selfLink], false);
        break;
      case "export-yaml":
        await itemHelpers.handleExportYaml([selfLink], false);
        break;
      case "export-json-slim":
        await itemHelpers.handleExportJSON([selfLink], true);
        break;
      case "export-yaml-slim":
        await itemHelpers.handleExportYaml([selfLink], true);
        break;
      case "export-terraform":
        setTerraformImportCommand(await itemHelpers.handleExportTerraform([selfLink]));
        break;
      case "export-k8s-crd":
        setAction("k8sCrdExport");
        break;
      case "download-scripts":
        setAction("download-scripts");
        break;
      case "delete":
        setAction("delete");
        break;
      default:
        break;
    }
  }

  const items: ItemType<MenuItemType>[] = [];
  items.push({ key: "view", label: "View" });
  if (protocolVersion === "v2") {
    items.push({ key: "download-scripts", label: "Download Scripts" });
  }
  const exportItem = {
    key: "ignore2",
    label: "Export",
    children: [
      { key: "export-json", label: "JSON" },
      { key: "export-yaml", label: "YAML" },
      { key: "export-json-slim", label: "JSON Slim" },
      { key: "export-yaml-slim", label: "YAML Slim" },
    ],
  };
  if (canExportToTerraform) {
    exportItem.children.push({ key: "export-terraform", label: "Terraform" });
  }
  if (canExportToK8sCrd) {
    exportItem.children.push({ key: "export-k8s-crd", label: "K8s CRD" });
  }
  items.push(exportItem);
  items.push({ key: "delete", label: "Delete", danger: true });

  function viewModalOnClose() {
    setAction("");
  }

  return (
    <>
      <div className="n-detail-header flex items-center justify-between pb-4 h-20 z-10">
        <div>
          <div data-testid="detail-kind" className="text-dark text-xs">
            {agent.kind.toUpperCase()}
          </div>
          <div className="flex items-center">
            <div data-testid="detail-name" className="text-2xl font-semibold leading-none">
              {agent.name}
            </div>
          </div>
        </div>
        <div className="flex-grow" />

        <div className="flex items-center gap-2">
          <div>{`Version ${agent.version}`}</div>
          <Button
            disabled={isFetching}
            loading={isFetching}
            onClick={() => refetch()}
            icon={<RefreshCw size={17} />}
            size="small"
            type="default"
          />
          {items.length > 0 ? (
            <Dropdown trigger={["click"]} menu={{ items: items, className: "menu", onClick: handleActionsClick }}>
              <Button type={"primary"} size="small" icon={<ChevronDown />} iconPosition="end">
                Actions
              </Button>
            </Dropdown>
          ) : null}
          <>
            <Tooltip
              title={[
                `Created: ${dateString(agent.created, { format: formatStrings.humanizeWAgo })}`,
                `Created (${timezoneLabel}): ${dateString(agent.created, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Created (UTC): ${dateString(agent.created, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
                `Last Modified: ${dateString(agent.lastModified, { format: formatStrings.humanizeWAgo })}`,
                `Last Modified (${timezoneLabel}): ${dateString(agent.lastModified, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Last Modified (UTC): ${dateString(agent.lastModified, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
              ]}
            >
              <div key={timeKey} className="ml-2">
                <DateString prefix={"Updated"} iso={agent.lastModified} format={formatStrings.humanizeWAgo} />
              </div>
            </Tooltip>
            <div>
              <Tooltip title={`Click to ${getIsLocked() ? "unlock" : "lock"} the object`} trigger={["hover"]}>
                <Switch
                  value={getIsLocked()}
                  aria-label="Toggle Lock"
                  onChange={() => setAction("lock")}
                  checkedChildren={<LockOutlined />}
                  unCheckedChildren={<UnlockOutlined />}
                />
              </Tooltip>
            </div>
          </>
        </div>
      </div>
      {action === "view" ? (
        <ViewModal
          kind={agent.kind}
          link={selfLink}
          visible={true}
          onClose={viewModalOnClose}
          title={agent.name}
          filename={itemHelpers.getFileName()}
        />
      ) : action === "delete" ? (
        <DeleteModal
          kind={agent.kind}
          itemName={agent.name}
          links={[selfLink]}
          onCancel={() => setAction("")}
          onComplete={() => setAction("")}
        />
      ) : action === "lock" ? (
        <Modal
          title={"Warning!"}
          open={true}
          maskClosable={false}
          onCancel={() => setAction("")}
          onOk={onLock}
          cancelButtonProps={{ disabled: isFetching }}
          okButtonProps={{ disabled: isFetching, loading: isFetching }}
        >
          <div>
            {getIsLocked()
              ? "Unlocking the object will allow deletion. Are you sure?"
              : "Locking the object will prevent deletion. Are you sure?"}
          </div>
        </Modal>
      ) : action === "tfImportCommand" ? (
        <TerraformImportModal command={terraformImportCommand} onClose={() => setAction("")} />
      ) : action === "k8sCrdExport" ? (
        <K8sCrdExportModal
          kind={agent.kind}
          links={[selfLink]}
          onClose={() => {
            setAction("");
          }}
        />
      ) : action === "download-scripts" ? (
        <DownloadScriptsModal agent={agent} visible={true} onClose={() => setAction("")} />
      ) : null}
    </>
  );
};
